import Highcharts from 'highcharts';
import { HighchartsReactRefObject } from 'highcharts-react-official';
import { TimelinePanelDto } from '@interfaces-clean/subject-monitoring.interface';
import {
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_dateSeries_DateSeries,
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_numericSeries_NumericSeries,
} from '@queries/generated/central-monitoring.generated';

export const CHART_COLORS = {
    BACKGROUND: '#FFFFFF',
    BACKGROUND_ALT: '#E8EDF2',
    BORDER_COLOR: '#757575',
    COLOUR_PRIMARY: '#3E536E',
    COLOUR_SECONDARY: '#984063',
} as const;

export const StatusToSvgName = new Map([
    ['completed', 'circle'],
    ['scheduled', 'circle-secondary'],
    ['out of window', 'square'],
    ['unscheduled', 'diamond'],
    ['missing', 'triangle'],
]);
export const DefaultStatusSvgName = 'triangle-down';

export type TimelineAxisSide = 'left' | 'right';

export type TimelineExtremes = {
    min: number;
    max: number;
};

export const TimelineAxisTypes = {
    Numeric: 'numeric',
    Date: 'date',
} as const;

export type TimelineAxisType = (typeof TimelineAxisTypes)[keyof typeof TimelineAxisTypes];

/**
 * Arbitrary because the `value` property is `unknown` and has to be figured out later at runtime...
 *
 * 1st string indexes data for specific panel. 2nd string indexes data for specific series.
 */
type ArbitrarySeriesData = TimelinePanelDto[string][string];

/**
 * The values of timeline date series which actually get used
 */
type TimelineDateSeriesDefinition = Pick<
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_dateSeries_DateSeries,
    'label' | 'type'
>;
/**
 * Groups together series definition with data for series
 */
export type TimelineDateSeries = {
    series: TimelineDateSeriesDefinition;
    data: ArbitrarySeriesData;
};

/**
 * The values of timeline numeric series which actually get used
 */
type TimelineNumericSeriesDefinition = Pick<
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis_numericSeries_NumericSeries,
    'label' | 'type' | 'yPlotField'
>;
/**
 * Groups together series definition with data for series
 */
export type TimelineNumericSeries = {
    series: TimelineNumericSeriesDefinition;
    data: ArbitrarySeriesData;
};

export type TimelineYAxisTitleOptionsCustomData = {
    title?: string;
    unit?: string;
};

export type TimelineYAxisCustomData = {
    title?: Highcharts.YAxisOptions & TimelineYAxisTitleOptionsCustomData;
};

export type TimelineSeriesCustomData = {
    isThreshold?: boolean;
    isFloat?: boolean;
    unit?: string;
    /**
     * Defines shape of `Highcharts.SeriesOptionsType` `custom` property for timeline series, which is otherwise `{ custom?: { [key: string]: any } }`
     */
    custom: {
        /**
         * Tracks which type of axis the series is for, since different axis types can use the same series types.
         *
         * E.g. a "scatter" series could be for either a numeric series or a date (single date) series.
         *
         * This property allows them to be differentiated.
         */
        axisType: TimelineAxisType;
    };
};

export type TimelineHighchartsReactRefObject = HighchartsReactRefObject & {
    // Add custom properties to chart
    chart: {
        customCrosshairElement?: Highcharts.SVGElement;
    };
};

export type CrosshairPosition = {
    // Position (in pixels) that crosshair is from left y-axis line
    plotX: number;
    // Value of x position hovered over
    value: Date;
};
